import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { insertVolunteerNotification } from '@/utils/volunteerNotificationInsert';
import { logger } from '@/utils/logger';

export type PendingMembershipRequest = {
  membershipId: string;
  volunteerId: string;
  createdAt: string;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
};

function mapRows(
  memberships: { id: string; volunteer_id: string; created_at: string }[],
  profilesById: Map<
    string,
    { first_name: string | null; last_name: string | null; profile_image: string | null }
  >
): PendingMembershipRequest[] {
  return memberships.map((m) => {
    const p = profilesById.get(m.volunteer_id);
    return {
      membershipId: m.id,
      volunteerId: m.volunteer_id,
      createdAt: m.created_at,
      firstName: p?.first_name ?? null,
      lastName: p?.last_name ?? null,
      profileImage: p?.profile_image ?? null,
    };
  });
}

export function useOrganizationMembershipRequests(organizationUserId: string | null) {
  const [requests, setRequests] = useState<PendingMembershipRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    if (!organizationUserId) {
      setRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: memRows, error: memError } = await supabase
        .from('organization_memberships')
        .select('id, volunteer_id, created_at')
        .eq('organization_id', organizationUserId)
        .eq('status', 'pending')
        .order('created_at', { ascending: true });

      if (memError) throw memError;

      const list = (memRows ?? []) as { id: string; volunteer_id: string; created_at: string }[];
      if (list.length === 0) {
        setRequests([]);
        return;
      }

      const ids = [...new Set(list.map((r) => r.volunteer_id))];
      const { data: profRows, error: profError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image')
        .in('id', ids);

      if (profError) throw profError;

      const profilesById = new Map<
        string,
        { first_name: string | null; last_name: string | null; profile_image: string | null }
      >();
      for (const row of profRows ?? []) {
        const r = row as {
          id: string;
          first_name: string | null;
          last_name: string | null;
          profile_image: string | null;
        };
        profilesById.set(r.id, {
          first_name: r.first_name,
          last_name: r.last_name,
          profile_image: r.profile_image,
        });
      }

      setRequests(mapRows(list, profilesById));
    } catch (e) {
      logger.error('useOrganizationMembershipRequests', e);
      setError(e instanceof Error ? e.message : 'Failed to load requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [organizationUserId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const setMembershipStatus = useCallback(
    async (
      membershipId: string,
      status: 'accepted' | 'rejected'
    ): Promise<{ ok: boolean; message?: string }> => {
      if (!organizationUserId) return { ok: false, message: 'Not signed in' };
      const acceptedVolunteerId =
        status === 'accepted'
          ? requests.find((r) => r.membershipId === membershipId)?.volunteerId
          : undefined;
      try {
        const { error: upErr } = await supabase
          .from('organization_memberships')
          .update({
            status,
            updated_at: new Date().toISOString(),
          })
          .eq('id', membershipId)
          .eq('organization_id', organizationUserId);

        if (upErr) return { ok: false, message: upErr.message };

        if (status === 'accepted' && acceptedVolunteerId) {
          const { data: orgProfile } = await supabase
            .from('profiles')
            .select('first_name')
            .eq('id', organizationUserId)
            .maybeSingle();
          const orgName =
            (orgProfile as { first_name?: string | null } | null)?.first_name?.trim() ||
            'the organization';
          insertVolunteerNotification({
            user_id: acceptedVolunteerId,
            title: 'Membership accepted',
            message: `You've been accepted into ${orgName}.`,
            type: 'organization_membership_accepted',
            data: {
              organization_id: organizationUserId,
              organization_name: orgName,
            },
            read: false,
          });
        }

        await fetchRequests();
        return { ok: true };
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Update failed';
        return { ok: false, message: msg };
      }
    },
    [organizationUserId, fetchRequests, requests]
  );

  return {
    pendingRequests: requests,
    loading,
    error,
    refetch: fetchRequests,
    acceptMembership: (membershipId: string) => setMembershipStatus(membershipId, 'accepted'),
    rejectMembership: (membershipId: string) => setMembershipStatus(membershipId, 'rejected'),
  };
}
